// ---------------------------------------------------------------------------
// CM-T.1: Community Spaces — grid of spaces with create modal
// ---------------------------------------------------------------------------
import { useState, useId } from "react";
import { Link } from "react-router";
import { useCommunitySpaces, createSpace, joinSpace, leaveSpace } from "@/hooks/use-community";
import { formatNumber } from "@/lib/i18n";
import {
  Users,
  MessageSquare,
  Plus,
  Loader2,
  Globe,
  X,
  LayoutGrid,
  BarChart3,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CreateSpaceForm {
  name: string;
  slug: string;
  description: string;
  accessType: "free" | "paid" | "tiered";
}

const ACCESS_TYPE_LABELS: Record<string, string> = {
  free: "Free",
  paid: "Paid",
  tiered: "Tiered",
};

const ACCESS_TYPE_COLORS: Record<string, string> = {
  free: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
  paid: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
  tiered: "bg-[var(--color-ai)]/10 text-[var(--color-ai)]",
};

const INITIAL_FORM: CreateSpaceForm = {
  name: "",
  slug: "",
  description: "",
  accessType: "free",
};

// ---------------------------------------------------------------------------
// Create Space Modal
// ---------------------------------------------------------------------------

function CreateSpaceModal({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState<CreateSpaceForm>(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameId = useId();
  const slugId = useId();
  const descId = useId();
  const accessId = useId();

  if (!isOpen) return null;

  const handleNameChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      name: value,
      slug: value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.slug.trim()) return;

    setIsSubmitting(true);
    setError(null);
    try {
      await createSpace({
        name: form.name.trim(),
        slug: form.slug.trim(),
        description: form.description.trim() || undefined,
        accessType: form.accessType,
      });
      setForm(INITIAL_FORM);
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create space");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Create Space</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/30 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor={nameId} className="block text-sm font-medium text-[var(--color-foreground)] mb-1">
              Name
            </label>
            <input
              id={nameId}
              type="text"
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              placeholder="General Discussion"
              required
            />
          </div>

          <div>
            <label htmlFor={slugId} className="block text-sm font-medium text-[var(--color-foreground)] mb-1">
              Slug
            </label>
            <input
              id={slugId}
              type="text"
              value={form.slug}
              onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              placeholder="general-discussion"
              required
            />
          </div>

          <div>
            <label htmlFor={descId} className="block text-sm font-medium text-[var(--color-foreground)] mb-1">
              Description
            </label>
            <textarea
              id={descId}
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] resize-none"
              placeholder="What is this space about?"
              rows={3}
            />
          </div>

          <div>
            <label htmlFor={accessId} className="block text-sm font-medium text-[var(--color-foreground)] mb-1">
              Access Type
            </label>
            <select
              id={accessId}
              value={form.accessType}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  accessType: e.target.value as CreateSpaceForm["accessType"],
                }))
              }
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            >
              <option value="free">Free</option>
              <option value="paid">Paid</option>
              <option value="tiered">Tiered</option>
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-muted)]/30 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !form.name.trim()}
              className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary)]/90 disabled:opacity-50 transition-colors"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Create Space"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Space Card
// ---------------------------------------------------------------------------

function SpaceCard({
  space,
  onMembershipChange,
}: {
  space: {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    accessType: string;
    memberCount: number;
    postCount: number;
    isMember: boolean;
  };
  onMembershipChange: () => void;
}) {
  const [isToggling, setIsToggling] = useState(false);

  const [leaveMessage, setLeaveMessage] = useState<string | null>(null);

  const handleToggleMembership = async () => {
    setIsToggling(true);
    setLeaveMessage(null);
    try {
      if (space.isMember) {
        if (space.accessType === "paid") {
          const confirmed = window.confirm("Cancel your paid membership? Access continues until the end of the billing period.");
          if (!confirmed) { setIsToggling(false); return; }
        }
        const result = await leaveSpace(space.id);
        if (result.cancelScheduled && result.message) {
          setLeaveMessage(result.message);
        }
      } else {
        // joinSpace handles Stripe redirect for paid spaces
        await joinSpace(space.id);
      }
      onMembershipChange();
    } catch {
      // Silently handle — refetch will show current state
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 hover:border-[var(--color-primary)]/30 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <Link
          to={`/community/${space.id}`}
          className="text-base font-semibold text-[var(--color-foreground)] hover:text-[var(--color-primary)] transition-colors"
        >
          {space.name}
        </Link>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${ACCESS_TYPE_COLORS[space.accessType] ?? ""}`}
        >
          {ACCESS_TYPE_LABELS[space.accessType] ?? space.accessType}
        </span>
      </div>

      {space.description && (
        <p className="text-sm text-[var(--color-muted-foreground)] line-clamp-2 mb-4">
          {space.description}
        </p>
      )}

      {leaveMessage && (
        <p className="text-xs text-[var(--color-warning)] mb-3">{leaveMessage}</p>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-xs text-[var(--color-muted-foreground)]">
          <span className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            {formatNumber(space.memberCount)}
          </span>
          <span className="flex items-center gap-1">
            <MessageSquare className="h-3.5 w-3.5" />
            {formatNumber(space.postCount)}
          </span>
        </div>
        <button
          type="button"
          onClick={handleToggleMembership}
          disabled={isToggling}
          className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
            space.isMember
              ? "border border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-destructive)]/30 hover:text-[var(--color-destructive)]"
              : "bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary)]/90"
          }`}
        >
          {isToggling ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : space.isMember ? (
            "Leave"
          ) : (
            "Join"
          )}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spaces Page
// ---------------------------------------------------------------------------

export default function CommunitySpacesPage() {
  const { spaces, isLoading, refetch } = useCommunitySpaces();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-[var(--color-primary)]" />
          <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Community Spaces</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/community/analytics"
            className="flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-muted)]/30 transition-colors"
          >
            <BarChart3 className="h-4 w-4" />
            Analytics
          </Link>
          <Link
            to="/community/members"
            className="flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-muted)]/30 transition-colors"
          >
            <Users className="h-4 w-4" />
            Members
          </Link>
          <button
            type="button"
            onClick={() => setIsCreateOpen(true)}
            className="flex items-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary)]/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Space
          </button>
        </div>
      </div>

      {/* Empty state */}
      {spaces.length === 0 && (
        <div className="text-center py-16">
          <LayoutGrid className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)]/30" />
          <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">
            No community spaces yet. Create the first one to get started.
          </p>
          <button
            type="button"
            onClick={() => setIsCreateOpen(true)}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary)]/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Space
          </button>
        </div>
      )}

      {/* Space grid */}
      {spaces.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {spaces.map((space) => (
            <SpaceCard key={space.id} space={space} onMembershipChange={refetch} />
          ))}
        </div>
      )}

      {/* Create modal */}
      <CreateSpaceModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={refetch}
      />
    </div>
  );
}
